import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Field } from '../components/ui/field';
import { Checkbox, Input, Textarea } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';

interface AiSettings {
  storefront: boolean;
  admin: boolean;
  content: boolean;
  monthlyBudgetEur: number;
  dailyBudgetEur: number;
  conversationBudgetEur: number;
  assistantModel: string;
  contentModel: string;
  glossary: string;
}

interface AiUsage {
  settings: AiSettings;
  monthEur: number;
  dayEur: number;
  bySurface: { surface: string; conversations: number; costEur: number }[];
}

const MODULES = ['storefront', 'admin', 'content'] as const;
const eur = (value: number) => `${value.toFixed(2)} €`;

/** AI: какие модули включены, бюджет и расход за месяц. */
export function AiPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const usage = useQuery({ queryKey: ['ai', 'usage'], queryFn: () => api<AiUsage>('/ai/usage') });
  const [form, setForm] = useState<AiSettings>();
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    if (usage.data && !form) setForm(usage.data.settings);
  }, [usage.data, form]);

  const save = async () => {
    if (!form) return;
    setError(undefined);
    setMessage(undefined);
    try {
      await api('/settings/ai', { method: 'PUT', body: form });
      setMessage(t('common.saved'));
      await queryClient.invalidateQueries({ queryKey: ['ai'] });
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  const number = (key: 'monthlyBudgetEur' | 'dailyBudgetEur' | 'conversationBudgetEur') => (
    <Field label={t(`ai.${key}`)}>
      {(id) => (
        <Input
          id={id}
          type="number"
          step="0.01"
          min="0"
          value={form?.[key] ?? 0}
          onChange={(e) => {
            setForm((f) => (f ? { ...f, [key]: Number(e.target.value) } : f));
          }}
          className="w-32"
        />
      )}
    </Field>
  );
  const used = usage.data
    ? usage.data.monthEur / Math.max(usage.data.settings.monthlyBudgetEur, 0.01)
    : 0;

  return (
    <>
      <PageHeader title={t('ai.title')} description={t('ai.hint')} />
      <div className="grid gap-6">
        <Card>
          <CardHeader title={t('ai.budget')} description={t('ai.budgetHint')} />
          <CardContent className="grid gap-3 text-sm">
            <p>
              {t('ai.month')}: <strong>{eur(usage.data?.monthEur ?? 0)}</strong> /{' '}
              {eur(usage.data?.settings.monthlyBudgetEur ?? 0)} · {t('ai.today')}:{' '}
              <strong>{eur(usage.data?.dayEur ?? 0)}</strong>
            </p>
            <div
              className="h-2 w-full max-w-md overflow-hidden rounded bg-muted"
              role="progressbar"
              aria-valuenow={Math.round(used * 100)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={t('ai.month')}
            >
              <div
                className={used >= 1 ? 'h-full bg-destructive' : 'h-full bg-primary'}
                style={{ width: `${String(Math.min(100, used * 100))}%` }}
              />
            </div>
            <table className="w-full max-w-md">
              <tbody>
                {(usage.data?.bySurface ?? []).map((s) => (
                  <tr key={s.surface} className="border-b last:border-0">
                    <td className="py-1.5">{t(`ai.modules.${s.surface as 'admin'}`)}</td>
                    <td className="text-right">
                      {t('ai.conversations', { count: s.conversations })}
                    </td>
                    <td className="text-right">{eur(s.costEur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {form ? (
          <Card>
            <CardHeader title={t('ai.settings')} description={t('ai.settingsHint')} />
            <CardContent className="grid gap-4 text-sm">
              <div className="grid gap-2">
                {MODULES.map((m) => (
                  <label key={m} className="flex items-center gap-2">
                    <Checkbox
                      checked={form[m]}
                      onChange={(e) => {
                        setForm({ ...form, [m]: e.target.checked });
                      }}
                    />
                    {t(`ai.modules.${m}`)}
                  </label>
                ))}
              </div>
              <div className="flex flex-wrap gap-3">
                {number('monthlyBudgetEur')}
                {number('dailyBudgetEur')}
                {number('conversationBudgetEur')}
              </div>
              <div className="flex flex-wrap gap-3">
                {(['assistantModel', 'contentModel'] as const).map((key) => (
                  <Field key={key} label={t(`ai.${key}`)}>
                    {(id) => (
                      <Input
                        id={id}
                        value={form[key]}
                        onChange={(e) => {
                          setForm({ ...form, [key]: e.target.value });
                        }}
                        className="w-64 font-mono"
                      />
                    )}
                  </Field>
                ))}
              </div>
              <Field label={t('ai.glossary')}>
                {(id) => (
                  <Textarea
                    id={id}
                    rows={3}
                    value={form.glossary}
                    placeholder={t('ai.glossaryHint')}
                    onChange={(e) => {
                      setForm({ ...form, glossary: e.target.value });
                    }}
                  />
                )}
              </Field>
              <div>
                <Button onClick={() => void save()}>{t('common.save')}</Button>
              </div>
              {message ? <Alert variant="success">{message}</Alert> : null}
              {error ? <Alert variant="destructive">{error}</Alert> : null}
            </CardContent>
          </Card>
        ) : null}
      </div>
    </>
  );
}
